import React, { useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import FilterListIcon from '@mui/icons-material/FilterList'
import { DateRangePicker } from 'react-date-range'
import { subDays, subMonths, format, startOfDay, endOfDay } from 'date-fns'
import Popover from '@mui/material/Popover'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../../redux/commonApiCalls'
import { filterButtonSx } from '../../../common/sharedStyles'

const today = () => new Date()
const QUICK_RANGES = [
  { label: 'Today',        range: () => ({ startDate: startOfDay(today()), endDate: endOfDay(today()) }) },
  { label: 'Yesterday',    range: () => ({ startDate: startOfDay(subDays(today(), 1)), endDate: endOfDay(subDays(today(), 1)) }) },
  { label: 'Last 3 Days',  range: () => ({ startDate: startOfDay(subDays(today(), 2)), endDate: endOfDay(today()) }) },
  { label: 'Last 7 Days',  range: () => ({ startDate: startOfDay(subDays(today(), 6)), endDate: endOfDay(today()) }) },
  { label: 'Last 30 Days', range: () => ({ startDate: startOfDay(subDays(today(), 29)), endDate: endOfDay(today()) }) },
].map(r => ({ ...r, isSelected: range => format(range.startDate, 'yyyy-MM-dd') === format(r.range().startDate, 'yyyy-MM-dd') && format(range.endDate, 'yyyy-MM-dd') === format(r.range().endDate, 'yyyy-MM-dd') }))

export default function InterventionFilters({ filters, onChange }) {
  const { data: merchantsRaw = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchants = merchantsRaw
    .filter(m => m.is_deleted === '0')
    .map(m => ({ label: m.companyName ? `${m.name} (${m.companyName})` : m.name, value: m.id }))

  const [anchorEl, setAnchorEl] = useState(null)
  const [dateRange, setDateRange] = useState([{
    startDate: filters?.dateRange?.from ? new Date(filters.dateRange.from) : subDays(new Date(), 7),
    endDate: filters?.dateRange?.to ? new Date(filters.dateRange.to) : new Date(),
    key: 'selection',
  }])

  const handleType = useCallback((_, val) => {
    if (val) onChange({ ...filters, type: val })
  }, [filters, onChange])

  const handleMerchant = useCallback((_, selected) => {
    onChange({ ...filters, merchantOids: selected.map(m => m.value) })
  }, [filters, onChange])

  const handleDateApply = useCallback(() => {
    setAnchorEl(null)
    onChange({
      ...filters,
      dateRange: {
        from: format(dateRange[0].startDate, 'yyyy-MM-dd'),
        to: format(dateRange[0].endDate, 'yyyy-MM-dd'),
      },
    })
  }, [dateRange, filters, onChange])

  const dateLabel = filters?.dateRange
    ? `${format(new Date(filters.dateRange.from), 'dd MMM')} – ${format(new Date(filters.dateRange.to), 'dd MMM yyyy')}`
    : 'Select dates'

  return (
    <Box className='filter-bar'>
      {/* Type toggle */}
      <ToggleButtonGroup value={filters?.type || 'BOTH'} exclusive onChange={handleType} size='small'>
        <ToggleButton value='BOTH'>Both</ToggleButton>
        <ToggleButton value='NDR'>NDR</ToggleButton>
        <ToggleButton value='NPR'>NPR</ToggleButton>
      </ToggleButtonGroup>

      {/* Date range */}
      <Button
        variant='outlined'
        size='small'
        startIcon={<FilterListIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {dateLabel}
      </Button>
      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 1 }}>
          <DateRangePicker
            ranges={dateRange}
            onChange={(item) => setDateRange([item.selection])}
            maxDate={new Date()}
            minDate={subMonths(new Date(), 6)}
            staticRanges={QUICK_RANGES}
            inputRanges={[]}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, gap: 1 }}>
            <Button size='small' sx={filterButtonSx('reset')} onClick={() => setAnchorEl(null)}>Cancel</Button>
            <Button variant='contained' size='small' sx={filterButtonSx('apply')} onClick={handleDateApply}>Apply</Button>
          </Box>
        </Box>
      </Popover>

      {/* Merchant multi-select */}
      <Autocomplete
        multiple
        size='small'
        options={merchants}
        value={merchants.filter(m => (filters?.merchantOids || []).includes(m.value))}
        getOptionLabel={(o) => o.label || ''}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        onChange={handleMerchant}
        renderInput={(params) => <TextField {...params} placeholder='Select merchants...' sx={{ minWidth: 220 }} />}
        limitTags={2}
        disableCloseOnSelect
        sx={{ minWidth: 250 }}
      />
    </Box>
  )
}
